<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch, nextTick } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunasPadrao = [
  // === Identificação ===
  { field: 'numeroPurchaseOrder', label: 'Nº PO', width: 130, sortable: true },
  { field: 'nomeReferencia', label: 'Referência', width: 200, sortable: true },
  { field: 'numeroProformaFornecedor', label: 'Proforma Forn.', width: 150, sortable: true, visible: false },
  { field: 'numeroContratoFornecedor', label: 'Contrato Forn.', width: 150, sortable: false, visible: false },

  // === Partes Relacionadas ===
  { field: 'nomeFornecedor', label: 'Fornecedor', width: 220, sortable: true },
  { field: 'nomeFilial', label: 'Filial', width: 150, sortable: true, visible: false },
  { field: 'nomeOperacao', label: 'Operação', width: 150, sortable: true, visible: false },
  { field: 'nomeLocalEstoque', label: 'Local Estoque', width: 160, sortable: false, visible: false },

  // === Datas ===
  { field: 'dataEmissao', label: 'Data Emissão', width: 120, sortable: true, align: 'center' },
  { field: 'previsaoProducao', label: 'Prev. Produção', width: 120, sortable: true, align: 'center', visible: false },
  { field: 'previsaoEmbarqueETD', label: 'ETD', width: 110, sortable: true, align: 'center', visible: false },
  { field: 'previsaoEmbarqueETA', label: 'ETA', width: 110, sortable: true, align: 'center', visible: false },

  // === Valores (Moeda Original) ===
  { field: 'lstMoeda', label: 'Moeda', width: 80, sortable: true, align: 'center' },
  { field: 'valorTotalOriginal', label: 'Valor Original', width: 140, sortable: true, align: 'right', formatter: (v) => formatarValor(v) },
  { field: 'valorTotalDesconto', label: 'Desconto', width: 120, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalFrete', label: 'Frete', width: 110, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalNegociado', label: 'Valor Negociado', width: 140, sortable: true, align: 'right', formatter: (v) => formatarValor(v) },

  // === Valores (Moeda Padrão) ===
  { field: 'valorTotalOriginalPadrao', label: 'V. Original (Padrão)', width: 160, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalFretePadrao', label: 'Frete (Padrão)', width: 140, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalDescontoPadrao', label: 'Desconto (Padrão)', width: 150, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalNegociadoPadrao', label: 'V. Negociado (Padrão)', width: 170, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },

  // === Valores (Moeda Gerencial) ===
  { field: 'valorTotalOriginalGerencial', label: 'V. Original (Gerencial)', width: 170, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalFreteGerencial', label: 'Frete (Gerencial)', width: 150, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalDescontoGerencial', label: 'Desconto (Gerencial)', width: 160, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'valorTotalNegociadoGerencial', label: 'V. Negociado (Gerencial)', width: 180, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },

  // === Câmbio ===
  { field: 'lstMoedaGerencial', label: 'Moeda Gerencial', width: 140, sortable: true, align: 'center', visible: false },
  { field: 'taxaCambioGerencialPadrao', label: 'Taxa Câmbio Ger.', width: 140, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'taxaCambioPadrao', label: 'Taxa Câmbio Padrão', width: 150, sortable: true, align: 'right', visible: false, formatter: (v) => formatarValor(v) },
  { field: 'lstTipoTaxaConversao', label: 'Tipo Taxa', width: 120, sortable: true, align: 'center', visible: false },
  { field: 'dataAlvoBuscaCambio', label: 'Data Alvo Câmbio', width: 140, sortable: true, align: 'center', visible: false },
  { field: 'dataReferenciaCambio', label: 'Data Ref. Câmbio', width: 140, sortable: true, align: 'center', visible: false },
  { field: 'lstTaxaCambioStatus', label: 'Status Câmbio', width: 130, sortable: true, align: 'center', visible: false },
  { field: 'lstFonteCambio', label: 'Fonte Câmbio', width: 130, sortable: true, visible: false },

  // === Listas / Classificações ===
  { field: 'lstStatusPurchaseOrder', label: 'Status', width: 120, sortable: true, align: 'center' },
  { field: 'lstFormaPagamento', label: 'Forma Pagamento', width: 150, sortable: true, align: 'center', visible: false },
  { field: 'lstIncoterm', label: 'Incoterm', width: 110, sortable: true, align: 'center', visible: false },
  { field: 'lstLocalIncoterm', label: 'Local Incoterm', width: 140, sortable: true, visible: false },

  // === Dados do Contrato ===
  { field: 'codigoContratoCompra', label: 'Cód. Contrato', width: 130, sortable: false, visible: true },
  { field: 'dataContrato', label: 'Data Contrato', width: 120, sortable: false, align: 'center', visible: false },
  { field: 'lstStatusContrato', label: 'Status Contrato', width: 130, sortable: false, align: 'center', visible: false },
  { field: 'valorTotalContrato', label: 'Valor Contrato', width: 140, sortable: false, align: 'right', visible: false, formatter: (v) => formatarValor(v) },

  // === Auditoria ===
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 150, sortable: false, visible: false },
  { field: 'nomeUsuarioModificacao', label: 'Modificado por', width: 150, sortable: false, visible: false },
  { field: 'nomeUsuarioAprovador', label: 'Aprovador', width: 150, sortable: false, visible: false },
  { field: 'tsCriacao', label: 'Data Criação', width: 150, sortable: true, visible: false },
  { field: 'tsModificacao', label: 'Data Modificação', width: 150, sortable: true, visible: false }
]

const colunas = computed(() => [
  ...colunasPadrao.map(c => {
    const slotMap = {
      dataEmissao: 'dataEmissao',
      lstMoeda: 'lstMoeda',
      lstStatusPurchaseOrder: 'status',
      previsaoProducao: 'previsaoProducao',
      previsaoEmbarqueETD: 'previsaoEmbarqueETD',
      previsaoEmbarqueETA: 'previsaoEmbarqueETA',
      dataContrato: 'dataContrato',
      dataAlvoBuscaCambio: 'dataAlvoBuscaCambio',
      dataReferenciaCambio: 'dataReferenciaCambio',
      tsCriacao: 'tsCriacao',
      tsModificacao: 'tsModificacao'
    }
    const mapped = slotMap[c.field]
    return mapped ? { ...c, slotName: mapped } : c
  }),
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstMoeda: (row) => row.lstMoedaTexto || row.lstMoeda || 'BRL',
    lstStatusPurchaseOrder: (row) => row.lstStatusPurchaseOrderTexto || row.lstStatusPurchaseOrder
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = (_field, _order) => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('PURCHASE_ORDERS_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('PURCHASE_ORDERS') && p.includes('COMPLETO'))
  )
})
const temPermissaoVisualizacao = computed(() => {
  if (authStore.pode('PURCHASE_ORDERS_VISUALIZACAO')) return true
  if (authStore.getPermsDoModulo('IMPORTACAO').some(p => p.includes('VISUALIZACAO'))) return true
  return false
})
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

// ============================================================
// Exportação de dados (AdminTable)
// ============================================================
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const emitNotification = (p) => emit('show-notification', p)

const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.busca) partes.push(`Busca: "${filtros.busca}"`)
  if (filtros.lstStatus) {
    const status = (listas.LstStatusPurchaseOrder || []).find(s => s.idListaClienteItem === filtros.lstStatus)
    partes.push(`Status: ${status?.textoLista || filtros.lstStatus}`)
  }
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

async function handleExportarDados (_formato) {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.totalCount ?? paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = injetarParametros(page)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

      const response = await api.get(`/PurchaseOrders?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]

    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[PurchaseOrderAdministrar] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['PurchaseOrder'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

    const response = await api.get(`/PurchaseOrders?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as purchase orders.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/PurchaseOrders/${item.idPurchaseOrder}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da purchase order.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idPurchaseOrder
  auditoriaSelecionada.nome = item.nomeReferencia
  mostrarAuditoria.value = true
}

const getMoedaIso = (texto) => {
  if (!texto) return 'BRL'
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : 'BRL'
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const obterCorStatus = (textoStatus) => {
  const texto = String(textoStatus ?? '').toUpperCase()

  if (texto.includes('ATIVO') || texto.includes('APROVADO') || texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO')) {
    return 'badge-ghost-success'
  }
  if (texto.includes('ABERTO') || texto.includes('PENDENTE') || texto.includes('DIGITAÇÃO')) {
    return 'badge-ghost-warning'
  }
  if (texto.includes('CANCELADO') || texto.includes('INATIVO') || texto.includes('REPROVADO')) {
    return 'badge-ghost-danger'
  }
  if (texto.includes('ANÁLISE') || texto.includes('ENVIADO')) {
    return 'badge-ghost-info'
  }
  if (texto.includes('PROCESSANDO')) {
    return 'badge-ghost-primary'
  }

  return 'badge-ghost-secondary'
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Gestão de Purchase Orders"
    subtitulo="Administração de purchase orders, incoterms e valores acordados com fornecedores"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Fornecedor ou Referência</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar por fornecedor ou referência..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusPurchaseOrder || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma purchase order encontrada."
      row-key="idPurchaseOrder"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
      modulo-layout="PurchaseOrders"
      :colunas-padrao="colunasPadrao"
      :colunas-fixas="['numeroPurchaseOrder']"
      :resolver-listas-tabela="'PurchaseOrder'"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Purchase Orders"
      :filtros-exportacao="descricaoFiltros"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
    >
      <template #idPurchaseOrder="{ row }">
        <span class="text-muted fw-medium">#{{ row.idPurchaseOrder }}</span>
      </template>

      <template #dataEmissao="{ row }">
        {{ formatarDataBR(row.dataEmissao) }}
      </template>

      <template #lstMoeda="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getMoedaIso(row.lstMoedaTexto || row.lstMoeda) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusPurchaseOrderTexto || row.lstStatusPurchaseOrder)">
          {{ row.lstStatusPurchaseOrderTexto || row.lstStatusPurchaseOrder || '-' }}
        </span>
      </template>

      <template #previsaoProducao="{ row }">
        {{ formatarDataBR(row.previsaoProducao) }}
      </template>

      <template #previsaoEmbarqueETD="{ row }">
        {{ formatarDataBR(row.previsaoEmbarqueETD) }}
      </template>

      <template #previsaoEmbarqueETA="{ row }">
        {{ formatarDataBR(row.previsaoEmbarqueETA) }}
      </template>

      <template #dataContrato="{ row }">
        {{ formatarDataBR(row.dataContrato) }}
      </template>

      <template #dataAlvoBuscaCambio="{ row }">
        {{ formatarDataBR(row.dataAlvoBuscaCambio) }}
      </template>

      <template #dataReferenciaCambio="{ row }">
        {{ formatarDataBR(row.dataReferenciaCambio) }}
      </template>

      <template #tsCriacao="{ row }">
        {{ formatarDataHoraBR(row.tsCriacao) }}
      </template>

      <template #tsModificacao="{ row }">
        {{ formatarDataHoraBR(row.tsModificacao) }}
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PurchaseOrder"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['PurchaseOrderProduto', 'PurchaseOrderCondicaoPg']"
    chave-estrangeira="IdPurchaseOrder"
    @fechar="mostrarAuditoria = false"
  />
</template>
